// modules.json — every widget's title, default size, renderer and flags. module-loader.js
// ported 1:1 in 05j-1: the same fetch (fresh every page load: `?v=` + no-store), the same
// "fall back to an empty list" on a failed load, and the same side-panel tile update after
// each load. The lookups are module-config-rules.ts (pure, tested).
//
// Dropped (dead here): `window.MODULE_CONFIG_PATH` and the `/modules/` path branch — they
// served Ben's standalone module preview pages, which this app doesn't have.

import { defaultSize, findModuleConfig, slugOf, type GridSize, type ModuleConfig } from "./module-config-rules.js";

export type { GridSize, ModuleConfig } from "./module-config-rules.js";

const CONFIG_URL = "modules.json";

let modulesConfig: ModuleConfig[] | null = null;
let modulesConfigPromise: Promise<ModuleConfig[]> | null = null;

// Async like module-loader.js's, so its callers wait the same number of ticks (presets.ts's
// loadPreset relies on the canvas changing a tick after a card drop's own collapse).
async function loadModulesConfig(): Promise<ModuleConfig[]> {
  if (modulesConfig) return modulesConfig;
  if (modulesConfigPromise) return modulesConfigPromise;

  modulesConfigPromise = fetch(`${CONFIG_URL}?v=${Date.now()}`, { cache: "no-store" })
    .then((response) => {
      if (!response.ok) throw new Error(`Could not load ${CONFIG_URL} (${response.status})`);
      return response.json() as Promise<unknown>;
    })
    .then((config) => {
      modulesConfig = Array.isArray(config) ? (config as ModuleConfig[]) : [];
      return modulesConfig;
    })
    // module-loader.js's fallback, kept: the page still runs (no widget can be added).
    .catch((error: unknown) => {
      console.warn("Could not load modules.json; falling back to static module map.", error);
      modulesConfig = [];
      return modulesConfig;
    })
    .finally(() => {
      modulesConfigPromise = null;
    });

  return modulesConfigPromise;
}

/** A title's (or slug's) entry; null before the list has loaded, or for an unknown title. */
export function getModuleConfigSync(identifier: string | null | undefined): ModuleConfig | null {
  return findModuleConfig(modulesConfig, identifier);
}

export function getSlug(title: string): string {
  return slugOf(getModuleConfigSync(title), title);
}

/** A title's size from modules.json, else the fallback (e.g. before it has loaded). */
export function getDefaultSize(identifier: string, fallback?: GridSize): GridSize {
  return defaultSize(getModuleConfigSync(identifier), fallback);
}

/** A side-panel tile's size: modules.json's, else its data-base-cols / rows (main.js
 *  getTemplateModuleSize). */
export function templateModuleSize(template: Element): GridSize {
  const title = template.getAttribute("data-title") ?? "";
  const fallbackCols = Number.parseInt(template.getAttribute("data-base-cols") || "1", 10);
  const fallbackRows = Number.parseInt(template.getAttribute("data-base-rows") || "1", 10);
  return getDefaultSize(title, { cols: fallbackCols, rows: fallbackRows });
}

/** Put each side-panel tile's modules.json size, slug and hover title on it. */
export function applyConfigToDashboardMenu(): void {
  if (!modulesConfig) return;
  document.querySelectorAll(".module-template").forEach((template) => {
    const cfg = getModuleConfigSync(template.getAttribute("data-title"));
    if (!cfg) return;
    template.setAttribute("data-base-cols", String(cfg.cols));
    template.setAttribute("data-base-rows", String(cfg.rows));
    template.setAttribute("data-module-slug", cfg.slug);
    template.setAttribute("title", `${cfg.title} · ${cfg.cols} × ${cfg.rows} grid`);
  });
}

/** Load modules.json (once) and update the side-panel tiles — every caller does both, as
 *  module-loader.js's whenReady did. */
export function whenModulesReady(): Promise<ModuleConfig[]> {
  return loadModulesConfig().then((config) => {
    applyConfigToDashboardMenu();
    return config;
  });
}

/** Where module-loader.js loaded (first of the legacy scripts), so its DOMContentLoaded
 *  listener stays first in line. */
export function installModuleConfig(): void {
  document.addEventListener("DOMContentLoaded", () => {
    void whenModulesReady();
  });
}
